
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IoHomeOutline,
  IoChevronDown,
  IoCheckmarkCircleOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import SearchBar2 from "./MyComps/searchBar2";
import {
  PROPERTY_PURPOSES,
  RESIDENTIAL_TYPES,
  COMMERCIAL_TYPES,
} from "./constants/properties";

const PRIMARY = "#ED213A";
const PRIMARY_DARK = "#D71931";
const TEXT_DARK = "#173B3A";
const BORDER = "#E8ECEB";
const SOFT_RED = "#FFF0F1";

const SearchBar = ({ onPurposeChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dropdownRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState("Buy");

  const [formData, setFormData] = useState({
    propertySubType: [],
    priceMin: "",
    city: "",
    priceMax: 100000000,
    purpose: "Buy",
  });

  const selectedTypes = formData.propertySubType;

  const handlePurposeChange = (text, index) => {
    setActiveIndex(index);
    setOpen(false);
    setActiveSubMenu(null);

    if (index === 6) {
      navigate("/projects/explore");
      return;
    }

    setSelectedPurpose(text);

    setFormData((prev) => ({
      ...prev,
      purpose: text,
    }));

    onPurposeChange?.(text);
  };

  const toggleSubMenu = (type) => {
    setActiveSubMenu((prev) => (prev === type ? null : type));
  };

  const handleSelectPropertyType = (type) => {
    setFormData((prev) => ({
      ...prev,
      propertySubType: prev.propertySubType.includes(type)
        ? prev.propertySubType.filter((item) => item !== type)
        : [...prev.propertySubType, type],
    }));
  };

  const handleRemovePropertyType = (type) => {
    setFormData((prev) => ({
      ...prev,
      propertySubType: prev.propertySubType.filter(
        (item) => item !== type
      ),
    }));
  };

  const handleClearTypes = () => {
    setFormData((prev) => ({
      ...prev,
      propertySubType: [],
    }));
  };

  const searchHandler = () => {
    if (!formData.city?.trim()) {
      return;
    }

    const params = new URLSearchParams({
      city: formData.city,
      propertySubType: selectedTypes.join(","),
      purpose: selectedPurpose,
    });

    window.open(`/p?${params.toString()}`, "_blank");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
        setActiveSubMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const residentialTypes = RESIDENTIAL_TYPES.filter((type) => {
    const purpose = PROPERTY_PURPOSES[activeIndex];

    if (purpose === "Rent/Lease" || purpose === "PG") {
      return type !== "Land / Plot";
    }

    if (purpose === "Plot/Land") {
      return type === "Land / Plot";
    }

    return true;
  });

  const commercialTypes = COMMERCIAL_TYPES.filter((type) =>
    PROPERTY_PURPOSES[activeIndex] === "Plot/Land"
      ? type.toLowerCase().includes("land")
      : true
  );

  const renderTypeOptions = (types) =>
    types.map((type) => {
      const isSelected = selectedTypes.includes(type);

      return (
        <MenuItem
          key={type}
          onClick={() => handleSelectPropertyType(type)}
          sx={{
            minHeight: 34,
            py: 0.7,
            px: 1.2,
            borderRadius: "9px",
            border: "1px solid",
            borderColor: isSelected ? PRIMARY : BORDER,
            bgcolor: isSelected ? SOFT_RED : "#FFFFFF",
            color: isSelected ? PRIMARY_DARK : TEXT_DARK,
            fontSize: "0.78rem",
            fontWeight: isSelected ? 700 : 500,
            whiteSpace: "normal",
            "&:hover": {
              bgcolor: SOFT_RED,
              borderColor: PRIMARY,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
            }}
          >
            {isSelected && (
              <IoCheckmarkCircleOutline size={15} />
            )}
            {type}
          </Box>
        </MenuItem>
      );
    });

  const selectorHeadingSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.2,
    py: 1,
    cursor: "pointer",
    borderRadius: "9px",
    "&:hover": {
      bgcolor: "#F8F8F8",
    },
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { xs: "100%", lg: 1500, xl: 1700 },
        mx: "auto",
        zIndex: 10,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          overflow: "visible",
          border: "1px solid rgba(23, 59, 58, 0.10)",
          borderRadius: { xs: "14px", md: "18px" },
          bgcolor: "#FFFFFF",
          boxShadow: {
            xs: "0 6px 20px rgba(23, 59, 58, 0.09)",
            md: "0 12px 38px rgba(23, 59, 58, 0.12)",
          },
        }}
      >
        {/* Compact property tabs */}
        {!isTablet && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              px: 2,
              pt: 1.1,
              pb: 0.9,
            }}
          >
            {PROPERTY_PURPOSES.map((text, index) => {
              const active = activeIndex === index;

              return (
                <Box
                  key={text}
                  component="button"
                  type="button"
                  onClick={() => handlePurposeChange(text, index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    px: 1.5,
                    py: 0.65,
                    border: "1px solid",
                    borderColor: active ? PRIMARY : "transparent",
                    borderRadius: "9px",
                    bgcolor: active ? SOFT_RED : "transparent",
                    color: active ? PRIMARY : TEXT_DARK,
                    fontFamily: "inherit",
                    fontSize: "0.84rem",
                    fontWeight: active ? 700 : 600,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: SOFT_RED,
                      color: PRIMARY,
                    },
                  }}
                >
                  {index === 0 && <IoHomeOutline size={16} />}
                  {text}
                </Box>
              );
            })}
          </Box>
        )}

        {!isTablet && (
          <Divider sx={{ borderColor: "#F0F2F1" }} />
        )}

        {/* Shorter, wider search row */}
        {isTablet ? (
          <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
            <SearchBar2
              isMobile={isMobile}
              isTablet={isTablet}
              searchHandler={searchHandler}
              formData={formData}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              width: "100%",
              minWidth: 0,
              px: 1,
              py: 0.5,
              gap: 0,
            }}
          >
            {/* Commercial purpose selector */}
            {PROPERTY_PURPOSES[activeIndex] === "Commercial" && (
              <>
                <ClickAwayListener
                  onClickAway={() => setDropdownOpen(false)}
                >
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      flex: "0 0 15%",
                      minWidth: 125,
                      px: 1.5,
                    }}
                  >
                    <Box
                      onClick={() =>
                        setDropdownOpen((prev) => !prev)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 1,
                        cursor: "pointer",
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      {selectedPurpose}
                      <IoChevronDown size={15} />
                    </Box>

                    {dropdownOpen && (
                      <Paper
                        elevation={0}
                        sx={{
                          position: "absolute",
                          top: "calc(100% + 10px)",
                          left: 4,
                          zIndex: 30,
                          width: 160,
                          p: 0.6,
                          border: `1px solid ${BORDER}`,
                          borderRadius: "12px",
                          boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                        }}
                      >
                        {["Buy", "Rent/Lease"].map((purpose) => (
                          <MenuItem
                            key={purpose}
                            onClick={() => {
                              setSelectedPurpose(purpose);
                              setFormData((prev) => ({
                                ...prev,
                                purpose,
                              }));
                              setDropdownOpen(false);
                            }}
                            sx={{
                              minHeight: 36,
                              borderRadius: "8px",
                              fontSize: "0.85rem",
                              color:
                                selectedPurpose === purpose
                                  ? PRIMARY
                                  : TEXT_DARK,
                            }}
                          >
                            {purpose}
                          </MenuItem>
                        ))}
                      </Paper>
                    )}
                  </Box>
                </ClickAwayListener>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ my: 1, borderColor: BORDER }}
                />
              </>
            )}

            {/* Property selector */}
            <ClickAwayListener
              onClickAway={() => {
                setOpen(false);
                setActiveSubMenu(null);
              }}
            >
              <Box
                ref={dropdownRef}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  flex: "0 0 25%",
                  minWidth: 180,
                  px: 1.5,
                }}
              >
                <Box
                  onClick={() => setOpen((prev) => !prev)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      borderRadius: "10px",
                      bgcolor: "#F8F8F8",
                      color: TEXT_DARK,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IoHomeOutline size={19} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.83rem",
                        fontWeight: 700,
                        color: TEXT_DARK,
                        lineHeight: 1.4,
                      }}
                    >
                      Property type
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.15,
                        fontSize: "0.74rem",
                        color: "#6B7776",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedTypes.length === 0
                        ? "All property types"
                        : selectedTypes.length === 1
                        ? selectedTypes[0]
                        : `${selectedTypes.length} selected`}
                    </Typography>
                  </Box>

                  <IoChevronDown
                    size={15}
                    style={{
                      color: "#687776",
                      transform: open ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </Box>

                {open && (
                  <Paper
                    elevation={0}
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 12px)",
                      left: 0,
                      width: 340,
                      maxWidth: "90vw",
                      maxHeight: 390,
                      overflowY: "auto",
                      zIndex: 40,
                      p: 1,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "14px",
                      boxShadow: "0 16px 42px rgba(0,0,0,0.13)",
                    }}
                  >
                    <Typography
                      sx={{
                        px: 1.2,
                        py: 0.8,
                        color: "#687776",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Select property type
                    </Typography>

                    {PROPERTY_PURPOSES[activeIndex] !== "Commercial" && (
                      <>
                        <Box
                          onClick={() => toggleSubMenu("residential")}
                          sx={selectorHeadingSx}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: TEXT_DARK,
                            }}
                          >
                            Residential
                          </Typography>
                          <IoChevronDown
                            style={{
                              transform:
                                activeSubMenu === "residential"
                                  ? "rotate(180deg)"
                                  : "none",
                            }}
                          />
                        </Box>

                        {activeSubMenu === "residential" && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 0.7,
                              p: 0.8,
                            }}
                          >
                            {renderTypeOptions(residentialTypes)}
                          </Box>
                        )}
                      </>
                    )}

                    {PROPERTY_PURPOSES[activeIndex] !==
                      PROPERTY_PURPOSES[2] && (
                      <>
                        <Divider sx={{ my: 0.5 }} />

                        <Box
                          onClick={() => toggleSubMenu("commercial")}
                          sx={selectorHeadingSx}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: TEXT_DARK,
                            }}
                          >
                            Commercial
                          </Typography>
                          <IoChevronDown
                            style={{
                              transform:
                                activeSubMenu === "commercial"
                                  ? "rotate(180deg)"
                                  : "none",
                            }}
                          />
                        </Box>

                        {activeSubMenu === "commercial" && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 0.7,
                              p: 0.8,
                            }}
                          >
                            {renderTypeOptions(commercialTypes)}
                          </Box>
                        )}
                      </>
                    )}

                    {selectedTypes.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                          pt: 1,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.76rem", color: "#687776" }}
                        >
                          {selectedTypes.length} selected
                        </Typography>

                        <Typography
                          onClick={handleClearTypes}
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            color: PRIMARY,
                            cursor: "pointer",
                          }}
                        >
                          Clear all
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                        px: 0.5,
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          setActiveSubMenu(null);
                        }}
                        sx={{
                          px: 2,
                          py: 0.8,
                          border: 0,
                          borderRadius: "9px",
                          bgcolor: PRIMARY,
                          color: "#FFFFFF",
                          fontFamily: "inherit",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          "&:hover": { bgcolor: PRIMARY_DARK },
                        }}
                      >
                        Apply
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ my: 1, borderColor: BORDER }}
            />

            {/* Location/search area receives the remaining width */}
            <Box
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                px: 1,
                "& > *": {
                  width: "100%",
                  minWidth: 0,
                },
              }}
            >
              <SearchBar2
                isMobile={isMobile}
                isTablet={isTablet}
                searchHandler={searchHandler}
                formData={formData}
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Compact selected-filter chips */}
      {selectedTypes.length > 0 && !isTablet && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.6,
            mt: 1,
            px: 0.5,
          }}
        >
          {selectedTypes.map((type) => (
            <Chip
              key={type}
              label={type}
              size="small"
              onDelete={() => handleRemovePropertyType(type)}
              sx={{
                height: 25,
                bgcolor: "#FFFFFF",
                border: `1px solid ${BORDER}`,
                borderRadius: "7px",
                color: TEXT_DARK,
                fontSize: "0.73rem",
                "& .MuiChip-deleteIcon": {
                  fontSize: 15,
                  color: "#687776",
                  "&:hover": { color: PRIMARY },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SearchBar;
